import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Download,
  LinkedIn,
  Share,
  Business,
  CalendarToday,
  Grade,
  Assessment,
  GitHub,
  VerifiedUser,
  Email
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/**
 * PUBLIC CERTIFICATE VERIFICATION PORTAL
 * No login required, mobile-responsive, SEO-friendly
 */
const CertificateVerification = () => {
  const { token: rawToken } = useParams();
  const token = decodeURIComponent(rawToken || '');
  const navigate = useNavigate();

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCertificate();
  }, [token]);

  const updateMetaTags = (cert) => {
    // Update page title
    document.title = `${cert.student_name} - Skill Genie Verified | ${cert.program_name}`;

    // Helper function to update or create meta tag
    const setMetaTag = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(property.startsWith('og:') ? 'property' : 'name', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // LinkedIn Open Graph meta tags
    setMetaTag('og:title', `${cert.student_name} - Skill Genie Verified`);
    setMetaTag('og:description', `${cert.student_name} completed ${cert.program_name} with grade ${cert.overall_grade}. Verified proof of work: ${cert.completed_tasks}/${cert.total_tasks} tasks completed. View certificate verification.`);
    setMetaTag('og:url', window.location.href);
    setMetaTag('og:type', 'profile');
    setMetaTag('og:site_name', 'The Skill Genie');

    // Use OG image if available, fallback to default
    if (cert.og_image_url) {
      setMetaTag('og:image', cert.og_image_url);
    } else {
      setMetaTag('og:image', 'https://placehold.co/1200x630?text=Skill+Genie+Certificate');
    }
    setMetaTag('og:image:width', '1200');
    setMetaTag('og:image:height', '630');
    setMetaTag('og:image:alt', `${cert.student_name} - Skill Genie Verified Certificate`);

    // Twitter Card meta tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', `${cert.student_name} - Skill Genie Verified`);
    setMetaTag('twitter:description', `Completed ${cert.program_name} with grade ${cert.overall_grade}`);
    if (cert.og_image_url) {
      setMetaTag('twitter:image', cert.og_image_url);
    }
  };

  const fetchCertificate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/certificates/verify/${token}`);

      if (response.data.success) {
        const cert = response.data.data;
        setCertificate(cert);

        // Update meta tags for LinkedIn rich preview
        updateMetaTags(cert);
      } else {
        setError(response.data.message || 'Certificate not found');
      }
    } catch (err) {
      console.error('Error fetching certificate:', err);
      setError(err.response?.data?.message || 'Failed to verify certificate. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (certificate?.certificate_pdf_url) {
      window.open(certificate.certificate_pdf_url, '_blank');
    }
  };

  const handleShareLinkedIn = async () => {
    if (!certificate) return;

    // Track share
    try {
      await axios.post(`${API_BASE_URL}/api/certificates/${token}/share`, {
        platform: 'LINKEDIN',
        sharedBy: certificate.student_id
      });
    } catch (err) {
      console.error('Failed to track share:', err);
    }

    // LinkedIn Share URL
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
    window.open(shareUrl, '_blank', 'width=600,height=600');
  };

  const handleAddToLinkedInProfile = () => {
    if (!certificate) return;

    // LinkedIn Add to Profile URL (requires LinkedIn certification API integration)
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certificate.program_name)}&organizationName=EvolveX%20Innovations&issueYear=${new Date(certificate.issue_date).getFullYear()}&issueMonth=${new Date(certificate.issue_date).getMonth() + 1}&certUrl=${encodeURIComponent(window.location.href)}&certId=${certificate.certificate_number}`;

    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  const handleContactStudent = () => {
    if (certificate?.student_email) {
      window.location.href = `mailto:${certificate.student_email}?subject=Hiring Inquiry - ${certificate.student_name}`;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f7fa'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => { window.location.href = 'https://theskillgenie.com'; }}>
          Go to Home
        </Button>
      </Container>
    );
  }

  if (!certificate) {
    return null;
  }

  const completionRate = certificate.total_tasks > 0
    ? Math.round((certificate.completed_tasks / certificate.total_tasks) * 100)
    : 0;

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Verified Badge Header */}
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, #1F8ACB 0%, #2ECC71 100%)',
            color: 'white',
            p: 3,
            borderRadius: 2,
            mb: 3,
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <VerifiedUser sx={{ fontSize: 48, mr: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              Verified by EvolveX
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.95 }}>
            This certificate has been verified and is authentic
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Left Column - Student Info & Photo */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent sx={{ textAlign: 'center', pt: 4 }}>
                <Avatar
                  src={certificate.student_photo_url}
                  alt={certificate.student_name}
                  sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  {certificate.student_name?.charAt(0)}
                </Avatar>

                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {certificate.student_name}
                </Typography>

                {certificate.student_roll_number && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Roll No: {certificate.student_roll_number}
                  </Typography>
                )}

                <Chip
                  icon={<Business />}
                  label={certificate.college_name}
                  color="primary"
                  sx={{ mt: 1, mb: 2 }}
                />

                {certificate.premium_partner && (
                  <Chip
                    label="Premium Partner"
                    size="small"
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000',
                      fontWeight: 'bold',
                      ml: 1
                    }}
                  />
                )}

                <Divider sx={{ my: 3 }} />

                {/* Action Buttons */}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleDownloadPDF}
                  sx={{ mb: 2 }}
                >
                  Download Certificate
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LinkedIn />}
                  onClick={handleAddToLinkedInProfile}
                  sx={{ mb: 2 }}
                >
                  Add to LinkedIn Profile
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Share />}
                  onClick={handleShareLinkedIn}
                  sx={{ mb: 2 }}
                >
                  Share on LinkedIn
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<Email />}
                  onClick={handleContactStudent}
                >
                  Contact for Hiring
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Certificate Details */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {certificate.program_name}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  <Chip
                    icon={<CheckCircle />}
                    label={`Grade: ${certificate.overall_grade}`}
                    color="success"
                    size="medium"
                  />
                  <Chip
                    icon={<Assessment />}
                    label={`Score: ${certificate.performance_score}%`}
                    color="primary"
                    size="medium"
                  />
                  <Chip
                    icon={<CalendarToday />}
                    label={`Completed: ${new Date(certificate.completion_date).toLocaleDateString()}`}
                    variant="outlined"
                    size="medium"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Proof of Work Section */}
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  📊 Proof of Work
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#f0f9ff',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {certificate.completed_tasks}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tasks Completed
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#f0fdf4',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {completionRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completion Rate
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#fefce8',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {certificate.github_commits}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Git Commits
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#faf5ff',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h4" fontWeight="bold" color="secondary.main">
                        {certificate.github_prs}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pull Requests
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* HOD Approval */}
                {certificate.hod_approved && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: '#e8f5e9',
                      borderRadius: 2,
                      borderLeft: '4px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="success" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Verified by Head of Department
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {certificate.hod_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Approved on {new Date(certificate.hod_approved_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Certificate Metadata */}
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Certificate Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Certificate ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {certificate.certificate_number}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Issue Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {new Date(certificate.issue_date).toLocaleDateString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Views
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {certificate.views_count || 0} views
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Shares
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {certificate.shares_count || 0} shares
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  This certificate is issued by EvolveX Innovations in partnership with {certificate.college_name}.
                  To verify authenticity, visit: {window.location.origin}/verify/{certificate.secure_token}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CertificateVerification;
